import type { Bill, BillLine, UpdateBillLineData } from '../types/bill';
import { canEditBill } from './billEditability';
import { payloadUnitPrice, type LinePriceSource } from './linePricing';

/** Line shape used by bill form payload builders. */
export interface BillFormLine {
  bill_line_id?: number;
  item_id: number | null;
  notes: string;
  qty: number;
  original_qty: number;
  unit_price: number;
  line_amount?: number | null;
  price_source?: LinePriceSource;
  discount_percent: number;
  description: string;
  sold_qty: number;
  min_allowed_qty: number;
  can_remove: boolean;
  markedForRemoval: boolean;
}

export const canCostCorrectBill = (): boolean =>
  localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const amountsEqual = (a: string, b: string) =>
  Math.abs(parseFloat(a) - parseFloat(b)) < 0.0001;

export const billHasConsumedInventory = (
  bill: Pick<Bill, 'lines'>
): boolean =>
  (bill.lines ?? []).some((line) => parseFloat(line.sold_qty ?? '0') > 0);

export const isCostCorrectionEdit = (
  bill: Pick<Bill, 'status' | 'balance' | 'total_amount' | 'received_into_inventory' | 'lines'>
): boolean =>
  bill.status === 'OPEN' &&
  bill.received_into_inventory &&
  amountsEqual(bill.balance, bill.total_amount) &&
  billHasConsumedInventory(bill);

export const isFullAmendEdit = (
  bill: Pick<Bill, 'status' | 'balance' | 'total_amount' | 'received_into_inventory' | 'lines'>
): boolean =>
  bill.status === 'OPEN' &&
  bill.received_into_inventory &&
  amountsEqual(bill.balance, bill.total_amount) &&
  !billHasConsumedInventory(bill);

export const canEnterBillEditor = (
  bill: Pick<Bill, 'status' | 'balance' | 'total_amount' | 'received_into_inventory' | 'lines'>
): boolean => {
  if (!canEditBill(bill)) return false;
  if (isCostCorrectionEdit(bill)) return canCostCorrectBill();
  return true;
};

export const isLineActive = (line: Pick<BillFormLine, 'item_id' | 'notes'>) =>
  Boolean(line.item_id || line.notes.trim());

export const activeLines = (lines: BillFormLine[]) => lines.filter(isLineActive);

/** A line that does not yet exist on the bill (appended row). */
export const isNewLine = (line: Pick<BillFormLine, 'bill_line_id'>) =>
  line.bill_line_id == null;

/** True when at least one active row has no bill line id (candidate for append). */
export const hasAppendedLines = (lines: BillFormLine[]): boolean =>
  lines.some((line) => isNewLine(line) && isLineActive(line));

const numbersEqual = (a: number, b: number) => Math.abs(a - b) < 0.0001;

/** Whether an existing line differs from its stored bill line (or is being removed). */
export const existingLineChanged = (line: BillFormLine, original: BillLine): boolean => {
  if (line.markedForRemoval) return true;
  return (
    !numbersEqual(line.qty, parseFloat(original.qty)) ||
    !numbersEqual(line.unit_price, parseFloat(original.unit_price)) ||
    !numbersEqual(line.discount_percent, parseFloat(original.discount_percent)) ||
    line.description.trim() !== (original.description ?? '').trim() ||
    line.notes.trim() !== (original.notes ?? '').trim()
  );
};

/** True when any existing (id-bearing) line was edited or removed. */
export const hasCostCorrectionChanges = (
  lines: BillFormLine[],
  bill: Pick<Bill, 'lines'>
): boolean => {
  const originals = new Map((bill.lines ?? []).map((line) => [line.id, line]));
  return lines.some((line) => {
    if (isNewLine(line) || !isLineActive(line)) return false;
    const original = line.bill_line_id != null ? originals.get(line.bill_line_id) : undefined;
    if (!original) return false;
    return existingLineChanged(line, original);
  });
};

export type ConsumedUpdateMode = 'cost_correction' | 'append' | 'conflict';

/**
 * Decide how a consumed-bill save should route. The API forbids mixing an
 * append with a cost correction in one request, so a mixed edit is a conflict.
 */
export const resolveConsumedUpdateMode = (
  lines: BillFormLine[],
  bill: Pick<Bill, 'lines'>
): ConsumedUpdateMode => {
  if (!hasAppendedLines(lines)) return 'cost_correction';
  return hasCostCorrectionChanges(lines, bill) ? 'conflict' : 'append';
};

export const survivingCostCorrectionLines = (lines: BillFormLine[]) =>
  lines.filter((line) => isLineActive(line) && !line.markedForRemoval);

const lineToPayloadFields = (line: BillFormLine) => ({
  ...(line.item_id ? { item_id: line.item_id } : {}),
  qty: line.qty,
  unit_price: payloadUnitPrice({
    unit_price: line.unit_price,
    line_amount: line.line_amount ?? null,
    qty: line.qty,
    price_source: line.price_source ?? 'rate',
  }),
  discount_percent: line.discount_percent,
  description: line.description.trim() || null,
  notes: line.notes.trim() || null,
});

/** Full replace payload for draft and unconsumed open amend. */
export const buildAmendLinePayload = (lines: BillFormLine[]): UpdateBillLineData[] =>
  activeLines(lines).map((line) => lineToPayloadFields(line));

/** In-place payload for cost correction — preserves line count and order. */
export const buildCostCorrectionLinePayload = (lines: BillFormLine[]): UpdateBillLineData[] =>
  lines
    .filter((line) => isLineActive(line) && line.bill_line_id != null)
    .map((line) => {
      const id = line.bill_line_id!;
      if (line.markedForRemoval) {
        return {
          id,
          ...(line.item_id ? { item_id: line.item_id } : {}),
          _remove: true,
        } as UpdateBillLineData;
      }
      return { id, ...lineToPayloadFields(line) };
    });

/**
 * Append payload for consumed bills: existing lines sent by `id` unchanged,
 * plus new id-less rows. The server inserts the new lines and leaves the rest.
 */
export const buildAppendLinePayload = (lines: BillFormLine[]): UpdateBillLineData[] =>
  activeLines(lines).map((line) =>
    isNewLine(line)
      ? lineToPayloadFields(line)
      : { id: line.bill_line_id!, ...lineToPayloadFields(line) }
  );

export const mapBillLineToFormLine = (line: BillLine): BillFormLine => {
  const qty = parseFloat(line.qty);
  const soldQty = parseFloat(line.sold_qty ?? '0');
  const minAllowedQty = line.min_allowed_qty != null ? parseFloat(line.min_allowed_qty) : soldQty;

  return {
    bill_line_id: line.id,
    item_id: line.item_id,
    notes: line.notes ?? '',
    qty,
    original_qty: qty,
    unit_price: parseFloat(line.unit_price),
    discount_percent: parseFloat(line.discount_percent),
    description: line.description ?? '',
    sold_qty: soldQty,
    min_allowed_qty: minAllowedQty,
    can_remove: line.can_remove ?? false,
    markedForRemoval: false,
  };
};

export const costCorrectionRemoveBlockedReason = (
  line: BillFormLine,
  lines: BillFormLine[]
): string | null => {
  if (line.markedForRemoval) return null;
  if (!line.can_remove) {
    if (line.sold_qty > 0) {
      return `${line.sold_qty} unit(s) already sold — minimum qty ${line.min_allowed_qty}`;
    }
    return 'This line cannot be removed';
  }
  const otherSurvivors = survivingCostCorrectionLines(lines).filter(
    (row) => row.bill_line_id !== line.bill_line_id
  );
  if (otherSurvivors.length === 0) {
    return 'At least one line must remain on the bill';
  }
  return null;
};
